import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalTitle,
} from "@msys/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Grid, Stack, ToggleButton, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useCategories } from "../../categories/useCategories.js";
import type {
  OnboardingFlowStage,
  OnboardingViewer,
} from "./OnboardingFlowModal.js";
import { getCategoryIcon } from "../../categories/categoryIcons.js";
import type {
  OnboardingFlowWizardAction,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

interface Props {
  // The modal's already-loaded viewer; stages must not re-subscribe to the
  // modal query (each subscription can refetch the suggestions field).
  viewer: OnboardingViewer;
  state: OnboardingFlowWizardState;
  dispatch: React.Dispatch<OnboardingFlowWizardAction>;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export const OnboardingFlowVerticalsStage = ({
  viewer,
  state,
  dispatch,
  setStage,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { categories: allCategories, loading: categoriesLoading } =
    useCategories();

  const verticals = React.useMemo(
    () =>
      allCategories
        .filter(c => !c.parentId)
        .slice()
        .sort((a, b) => a.rank - b.rank),
    [allCategories]
  );

  // Defense-in-depth auto-skip for direct-URL navigation. CLIENT orgs and
  // orgs with pre-existing offered services should never see this stage —
  // the primary CLIENT routing branches at the form stage, and populated
  // orgs are excluded here so the bulk REPLACE-ALL
  // setOrganisationOfferedServices mutation never clobbers their existing
  // rows. Check the CLIENT branch first; both produce the same
  // `?ofs=success` outcome.
  React.useEffect(() => {
    if (viewer.organisation.organisationType !== "CRAFTSMAN") {
      setStage("success");
      return;
    }
    if (viewer.organisation.offeredServices.length > 0) {
      setStage("success");
    }
  }, [viewer, setStage]);

  if (categoriesLoading) {
    return <LoadingSpinner />;
  }
  if (
    viewer.organisation.organisationType !== "CRAFTSMAN" ||
    viewer.organisation.offeredServices.length > 0
  ) {
    return null;
  }

  const selected = new Set(state.selectedVerticals);

  return (
    <>
      <ModalTitle
        title={t("Which categories do you offer?", {
          ns: "OrganisationProfile",
        })}
      />
      <ModalContent>
        <Stack spacing={2}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t(
              "Select the verticals that describe your business. You can refine later.",
              { ns: "OrganisationProfile" }
            )}
          </Typography>
          <Grid container spacing={2} columns={{ md: 3, xs: 2 }}>
            {verticals.map(vertical => (
              <Grid key={vertical.id} size={1}>
                <VerticalTile
                  id={vertical.id}
                  name={vertical.name}
                  value={vertical.id}
                  selected={selected.has(vertical.id)}
                  onClick={() =>
                    dispatch({
                      type: "toggleVertical",
                      categoryId: vertical.id,
                    })
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </ModalContent>
      <ModalActions
        actionButtons={[
          {
            label: t("Back", { ns: "Global" }),
            handleClick: () => setStage("form"),
            buttonProps: {
              color: "primary",
              variant: "text",
              startIcon: <ChevronLeftIcon />,
            },
          },
          {
            label: t("Continue", { ns: "Global" }),
            handleClick: () => setStage("projectTypes"),
            buttonProps: {
              color: "primary",
              variant: "contained",
              endIcon: <ChevronRightIcon />,
              disabled: state.selectedVerticals.length === 0,
            },
          },
        ]}
      />
    </>
  );
};

interface VerticalTileProps {
  id: string;
  name: string;
  value: string;
  selected: boolean;
  onClick: () => void;
}

const VerticalTile = ({
  id,
  name,
  value,
  selected,
  onClick,
}: VerticalTileProps) => {
  const Icon = getCategoryIcon(id);
  return (
    <ToggleButton
      aria-label={name}
      aria-pressed={selected}
      value={value}
      selected={selected}
      onChange={onClick}
      sx={{
        flex: 1,
        width: "100%",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Icon color={selected ? "primary" : "action"} fontSize="large" />
      <Typography variant="body2" color={selected ? "primary" : "text.primary"}>
        {name}
      </Typography>
    </ToggleButton>
  );
};
